// "Fetch every page for a client-side bulk op" primitives. The legacy catalog
// saga fetched all pages for XLSX ops 5-at-a-time (page size 250); that contract
// now lives here in ONE place instead of being copy-pasted per endpoint.
//
// Two paging strategies live side by side:
//   - `fetchAllPages`         — parallel `$skip`/offset paging in 5-wide chunks.
//                               Cheap at shallow depth; used by the catalog
//                               fetch-all hooks and the add-products fan-out.
//   - `fetchAllPagesByCursor` — sequential Atlas `searchAfter` cursor paging.
//                               Flat per-page latency at any depth; used where
//                               catalogs run 30K–60K+ deep (vendor products
//                               select-all / export) and deep `$skip` re-scans
//                               from the top would dominate.

export const FETCH_ALL_PAGE_SIZE = 250;
export const FETCH_ALL_CHUNK = 5;
// Extra pages of tolerance (on top of the page count the FIRST response's total
// implies) for `total` drifting from concurrent writes mid-fetch. Fixed at the
// first page rather than recomputed each pass — a `total` that keeps climbing in
// step with the fetch would otherwise let the cap chase a moving target and never
// catch a stuck cursor / never-converging total.
export const FETCH_ALL_ITERATION_BUFFER = 5;

interface FetchAllOptions<TItem> {
  /** Fetches a single 1-based page; resolves the page payload. */
  fetchPage: (page: number) => Promise<{ items: TItem[]; total: number }>;
  pageSize?: number;
  chunk?: number;
  /**
   * Called after each page resolves with the number of pages completed so far
   * and the total page count (known once page 1 returns). Lets callers drive a
   * determinate progress bar.
   */
  onProgress?: (completedPages: number, totalPages: number) => void;
}

/**
 * Loads every item across all pages. Page 1 yields the total; the remaining
 * pages are fetched in bounded-concurrency batches (default 5) to avoid
 * hammering the backend.
 */
export async function fetchAllPages<TItem>({
  fetchPage,
  pageSize = FETCH_ALL_PAGE_SIZE,
  chunk = FETCH_ALL_CHUNK,
  onProgress,
}: FetchAllOptions<TItem>): Promise<TItem[]> {
  const first = await fetchPage(1);
  const all: TItem[] = [...first.items];
  const lastPage = Math.max(1, Math.ceil((first.total || all.length) / pageSize));
  let completed = 1;
  onProgress?.(completed, lastPage);

  for (let page = 2; page <= lastPage; page += chunk) {
    // Once any request in a batch fails we stop ticking progress, but still let
    // every in-flight request SETTLE before propagating the rejection. Otherwise
    // a sibling that resolves after the failure would fire `onProgress` after the
    // caller has already handled the error and torn down its progress UI — e.g.
    // ProductMapping's downloadCurrent clears its bar in `finally`, and a late
    // tick would resurrect it.
    let batchFailed = false;
    const batch: Promise<{ items: TItem[]; total: number }>[] = [];
    for (let p = page; p < page + chunk && p <= lastPage; p += 1) {
      batch.push(
        fetchPage(p).then(
          (res) => {
            if (!batchFailed) {
              completed += 1;
              onProgress?.(completed, lastPage);
            }
            return res;
          },
          (err) => {
            batchFailed = true;
            throw err;
          },
        ),
      );
    }
    const results = await Promise.allSettled(batch);
    const rejected = results.find(
      (r): r is PromiseRejectedResult => r.status === 'rejected',
    );
    if (rejected) throw rejected.reason;
    for (const result of results) {
      if (result.status === 'fulfilled') all.push(...result.value.items);
    }
  }
  return all;
}

/**
 * Runs `task` over every item in bounded-concurrency chunks (default 5).
 * Same throttle as `fetchAllPages` but for an already-known item list — used
 * by the add-products variant fan-out so selecting N products doesn't fire N
 * simultaneous requests. `onProgress` ticks once per completed task.
 */
export async function runInChunks<TItem, TResult>(
  items: TItem[],
  task: (item: TItem) => Promise<TResult>,
  options: { chunk?: number; onProgress?: () => void } = {},
): Promise<TResult[]> {
  const { chunk = FETCH_ALL_CHUNK, onProgress } = options;
  const results: TResult[] = [];
  for (let i = 0; i < items.length; i += chunk) {
    const slice = items.slice(i, i + chunk);
    const batch = await Promise.all(
      slice.map(async (item) => {
        const result = await task(item);
        onProgress?.();
        return result;
      }),
    );
    results.push(...batch);
  }
  return results;
}

interface CursorPageResult<TItem> {
  items: TItem[];
  total: number;
  paginationToken?: string | null;
}

interface FetchAllPagesByCursorOptions<TItem> {
  /** Fetches one page; `page` is the 1-based `$skip` fallback pointer. */
  fetchPage: (args: {
    page: number;
    paginationToken: string | null | undefined;
  }) => Promise<CursorPageResult<TItem>>;
  pageSize?: number;
  iterationBuffer?: number;
  /** Ticks after each page with the running totals. */
  onProgress?: (progress: { processed: number; total: number }) => void;
  /** Polled before each fetch; return true to abort and return what's collected. */
  shouldCancel?: () => boolean;
}

export interface FetchAllPagesByCursorResult<TItem> {
  items: TItem[];
  total: number;
  /** True when the iteration cap was hit before paging completed. */
  exceededIterationCap: boolean;
  /** True when `shouldCancel` aborted the run early. */
  cancelled: boolean;
}

/**
 * Loads every item across all pages by following the server's `searchAfter`
 * pagination token, with a `$skip` page fallback. The server prefers a supplied
 * token over `page`, so the page pointer stays inert while a token flows and
 * transparently resumes `$skip` paging if tokens stop coming back (a backend
 * without cursor support). Stops when a short page or the declared `total` is
 * reached, when `shouldCancel` fires, or when the iteration cap trips (surfaced
 * as `exceededIterationCap` for the caller to act on). Sequential by nature —
 * flat per-page cost is the point, not concurrency.
 */
export async function fetchAllPagesByCursor<TItem>({
  fetchPage,
  pageSize = FETCH_ALL_PAGE_SIZE,
  iterationBuffer = FETCH_ALL_ITERATION_BUFFER,
  onProgress,
  shouldCancel,
}: FetchAllPagesByCursorOptions<TItem>): Promise<FetchAllPagesByCursorResult<TItem>> {
  const all: TItem[] = [];
  let total = 0;
  let maxIterations: number | null = null;
  let page = 1;
  let paginationToken: string | null | undefined;
  let iterations = 0;
  let exceededIterationCap = false;

  for (;;) {
    if (shouldCancel?.()) {
      return { items: all, total, exceededIterationCap: false, cancelled: true };
    }
    iterations += 1;
    const resp = await fetchPage({ page, paginationToken });
    total = resp.total ?? 0;
    maxIterations ??= Math.ceil(total / pageSize) + iterationBuffer;
    const batch = resp.items ?? [];
    all.push(...batch);
    onProgress?.({ processed: all.length, total });
    if (batch.length < pageSize || all.length >= total) break;
    if (iterations >= maxIterations) {
      exceededIterationCap = true;
      break;
    }
    // Advance the $skip fallback pointer even while a token is active (inert in
    // cursor mode) and mirror the token exactly — never carry a stale one forward.
    page += 1;
    paginationToken = resp.paginationToken;
  }

  return { items: all, total, exceededIterationCap, cancelled: false };
}
